import type {SxProps, Theme} from "@mui/material/styles"

/**
 * Type definition for the input of the `mergeSx` function, allowing for various forms of `SxProps`.
 */
type SxInput = SxProps<Theme> | false | null | undefined

/**
 * Merges two MUI `SxProps` objects into one, allowing for the combination of multiple style definitions.
 *
 * @param {...SxInput[]} sxs - The `SxProps` objects to merge.
 * @returns {SxProps<Theme>} The merged `SxProps` object containing styles from all input objects.
 */
export const mergeSx = (...sxs: SxInput[]): SxProps<Theme> => sxs.flat() as SxProps<Theme>

/**
 * Defines the available sizes for NdComponents, providing a consistent way to specify component sizes across the application.
 */
export const NdComponentSize = {
    SMALL: "small",
    MEDIUM: "medium",
    LARGE: "large"
} as const
export type NdComponentSize = (typeof NdComponentSize)[keyof typeof NdComponentSize]

const NdComponentSizeAlias = {
    sm: NdComponentSize.SMALL,
    md: NdComponentSize.MEDIUM,
    lg: NdComponentSize.LARGE
} as const
type NdComponentSizeAlias = keyof typeof NdComponentSizeAlias

export type NdComponentSizeInput = NdComponentSize | NdComponentSizeAlias

/**
 * Reads a size, spelled either in full or as its short alias, into the one name it stands for.
 *
 * @param {NdComponentSizeInput} size - The size, as `"small"` or as `"sm"`.
 * @returns {NdComponentSize} The size under its full name.
 */
export const resolveNdComponentSize = (size: NdComponentSizeInput): NdComponentSize =>
    size in NdComponentSizeAlias ? NdComponentSizeAlias[size as NdComponentSizeAlias] : (size as NdComponentSize)
